import React, { memo, useEffect, useState, useCallback } from 'react';
import { Box, Button, Flex, Heading, Image, Text, IconButton, useBreakpointValue } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);
const MotionButton = motion(Button);

const desktopSlides = [
  '/hero_bg_1.webp',
  '/hero_bg_2.webp',
  '/hero-bg.webp'
];

const mobileSlides = [
  '/hero_bg_1_mobile.webp',
  '/hero_bg_2_mobile.webp',
   '/hero-bg_mobile.webp'
];

const Hero = memo(({ startAnimation = false }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const slides = isMobile ? mobileSlides : desktopSlides;

  // Autoplay logic
  useEffect(() => {
    let interval;
    if (isAutoPlaying && startAnimation) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, startAnimation, slides.length]);

  const nextSlide = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    // Resume autoplay after 8 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 8000);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  }, [slides.length]);

  // Text animations
  const textVariants = {
    hidden: { opacity: 0, y: 30, filter: "blur(10px)" },
    visible: { 
      opacity: 1, 
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 1.2, ease: "easeOut", delay: 0.5 }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20, x: "-50%" },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      x: "-50%",
      transition: { duration: 0.8, delay: 1.2, type: "spring", bounce: 0.4 }
    }
  };

  // Ken Burns effect for background
  const bgVariants = {
    initial: { scale: 1.1, opacity: 0 },
    animate: { 
      scale: 1, 
      opacity: 1,
      transition: { duration: 1.5, ease: "easeOut" }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 1 }
    }
  };

  return (
    <Box 
      position="relative" 
      h="100vh" 
      w="100%" 
      overflow="hidden" 
      bg="black"
    >
      {/* Slideshow Background */}
      <AnimatePresence mode='popLayout'>
        <MotionBox
          key={`${isMobile ? 'mobile' : 'desktop'}-${currentSlide}`}
          position="absolute"
          top="0"
          left="0"
          w="100%"
          h="100%"
          variants={bgVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          zIndex={1}
        >
          <Image
            src={slides[currentSlide]}
            alt={`Slide ${currentSlide + 1}`}
            objectFit="cover"
            w="100%"
            h="100%"
            filter="brightness(0.7)" // Darken slightly for text readability
            loading="eager"
          />
        </MotionBox>
      </AnimatePresence>

      {/* Overlay Content */}
      <Box position="absolute" top="0" left="0" w="100%" h="100%" zIndex={2} pointerEvents="none">
        
        {/* Top Left Text */}
        <MotionBox
          position="absolute"
          top={{ base: "15%", md: "15%" }}
          left={{ base: "5%", md: "5%" }}
          maxW={{ base: "80%", md: "500px" }}
          initial="hidden"
          animate={startAnimation ? "visible" : "hidden"}
          variants={textVariants}
        >
          <Heading
            as="h1"
            color="white"
            fontFamily="'Cormorant', serif"
            fontWeight="700"
            fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
            lineHeight="1.1"
            textShadow="0 2px 10px rgba(0,0,0,0.5)"
          >
            Discover Our Exquisite Jewelry Collection
          </Heading>
        </MotionBox>

        {/* Bottom Right Text */}
        <MotionBox
          position="absolute"
          bottom={{ base: "15%", md: "10%" }}
          right={{ base: "5%", md: "5%" }}
          maxW={{ base: "90%", md: "400px" }}
          textAlign="right"
          initial="hidden"
          animate={startAnimation ? "visible" : "hidden"}
          variants={textVariants}
        >
          <Text
            color="white"
            fontFamily="'Cormorant', serif"
            fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
            fontWeight="700"
            lineHeight="1.1"
            textShadow="0 2px 10px rgba(0,0,0,0.5)"
          >
             For the ones who wear presence.
          </Text>
        </MotionBox>

        {/* Center Button - Positioned lower */}
        <MotionBox
          position="absolute"
          bottom={{ base: "28%", md: "28%" }}
          left="50%"
          initial="hidden"
          animate={startAnimation ? "visible" : "hidden"}
          variants={buttonVariants}
          pointerEvents="auto" // Re-enable clicks
        >
          <MotionButton
            size={{ base: "lg", md: "5xl", lg: "6xl" }}
            px={8}
            py={6}
            bg="white"
            color="black"
            fontFamily="'Zain', sans-serif"
            fontSize="lg"
            fontWeight="600"
            borderRadius="full"
            _hover={{ transform: "scale(1.05)", bg: "gray.100" }}
            _active={{ transform: "scale(0.95)" }}
            onClick={() => {
              const section = document.getElementById('product-categories');
              section?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Shop Our Collection
          </MotionButton>
        </MotionBox>
      </Box>

      {/* Navigation Arrows */}
      <IconButton
        aria-label="Previous Slide"
        icon={<FaChevronLeft size={24} />}
        position="absolute"
        left={{ base: "2", md: "8" }}
        top="50%"
        transform="translateY(-50%)"
        zIndex={3}
        variant="ghost"
        color="white"
        _hover={{ bg: "whiteAlpha.200" }}
        onClick={prevSlide}
        display={{ base: "none", md: "flex" }} // Hide on mobile if desired, or keep
      />
      <IconButton
        aria-label="Next Slide"
        icon={<FaChevronRight size={24} />}
        position="absolute"
        right={{ base: "2", md: "8" }}
        top="50%"
        transform="translateY(-50%)"
        zIndex={3}
        variant="ghost"
        color="white"
        _hover={{ bg: "whiteAlpha.200" }}
        onClick={nextSlide}
        display={{ base: "none", md: "flex" }}
      />
      
      {/* Mobile Navigation (Tap areas or smaller arrows) */}
      <Flex 
        display={{ base: "flex", md: "none" }} 
        position="absolute" 
        bottom="5%" 
        left="0" 
        w="100%" 
        justify="center" 
        zIndex={3} 
        gap={4}
      >
         <IconButton
            aria-label="Previous Slide"
            icon={<FaChevronLeft />}
            variant="outline"
            color="white"
            size="sm"
            onClick={prevSlide}
            borderColor="whiteAlpha.400"
          />
          <IconButton
            aria-label="Next Slide"
            icon={<FaChevronRight />}
            variant="outline"
            color="white"
            size="sm"
            onClick={nextSlide}
            borderColor="whiteAlpha.400"
          />
      </Flex>

    </Box>
  );
});

export default Hero;